/**
 * declinedRequest.js — PROD-983: an appointment request or a held reschedule the physician DECLINED
 * reads as declined in the patient app, never as scheduled, confirmed or pending.
 *
 * orella-api respond_to_appointment (action 'reject') writes:
 *   - an initial request (pending, or a held reschedule of the initial visit): the case's
 *     appointment_request gets status 'rejected' (+ response.rejectionReason); the named
 *     appointments row moves to 'rejected', which get_cases filters out of case.appointments and
 *     followUpAppointment (GET /appointments still returns it).
 *   - a held FOLLOW-UP reschedule (PROD-970): follow_up.appointmentStatus 'rejected',
 *     appointmentScheduled false, scheduledDate/scheduledTime removed, no join link; the case's
 *     appointment_request is left as it stands.
 * The case status itself is not changed by a decline (a held reschedule of a confirmed initial visit
 * leaves the case 'scheduled').
 *
 * Before this file the patient app had no reference to 'rejected' on any of these: Case Home showed
 * the declined request as Pending (with Check in / Change), the visit header kept its time, the
 * Visits tab listed the rejected row as an upcoming visit (Join for video), and the store's
 * follow-up merge put the stale local "scheduled" fields back over the server's cleared copy.
 *
 * Copy: reuses notifications.appointmentRejected.line (en + es approved). No new strings.
 * PROD-1137 (below caseHasDecline): the physician CANCELLED a visit he had confirmed; this file also
 * holds that API contract's field names, in one section.
 *
 * Import-free on purpose: scripts/check-rejected-status.mjs executes it.
 */

export const DECLINED = 'rejected'

/** True for the status the API writes when a physician declines a request or a held reschedule. */
export function isDeclined(status) {
  return String(status ?? '').trim().toLowerCase() === DECLINED
}

const asObject = (v) => {
  if (!v) return null
  if (typeof v === 'string') {
    try { const o = JSON.parse(v); return o && typeof o === 'object' ? o : null } catch { return null }
  }
  return typeof v === 'object' ? v : null
}

/** The case's appointment_request (initial request) was declined. Accepts the object or its JSON. */
export function requestDeclined(appointmentRequest) {
  const r = asObject(appointmentRequest)
  return !!r && isDeclined(r.status)
}

/** The case's follow_up was declined (a held follow-up reschedule, PROD-970). Object or JSON. */
export function followUpDeclined(followUp) {
  const f = asObject(followUp)
  return !!f && isDeclined(f.appointmentStatus)
}

/**
 * Either half of a case (raw API shape or store shape) says the physician declined, or (PROD-1137)
 * that the physician cancelled the visit he had confirmed. After either, the server's appointment
 * list is the truth: the store must not put a stale local booked row back over it.
 */
export function caseHasDecline(caseItem) {
  if (!caseItem || typeof caseItem !== 'object') return false
  return requestDeclined(caseItem.appointmentRequest || caseItem.appointment_request)
    || followUpDeclined(caseItem.followUp || caseItem.follow_up)
    || caseHasPhysicianCancel(caseItem)
}

// ═══ PROD-1137: THE PHYSICIAN CANCELLED A CONFIRMED VISIT — API CONTRACT ADAPTER ═══════════════
//
// orella-api POST /appointments/<id>/cancel (PLAN 1137 §1d rows 1, 8, 13; §2). It is the ONLY place in
// the patient app that knows the field names this route writes; a contract change is a fix here only.
//   - appointments row: status 'cancelled', modified_by 'doctor', status_history += { status:
//     'cancelled', previousStatus, changedBy: 'doctor' | 'ma', changedAt, reason, reasonCode }.
//   - initial visit: case appointment_request = { …, status: 'cancelled', cancelledBy: 'doctor',
//     cancelledAt, cancelReasonCode, cancelledVisit: { date, time, type } }, confirmed* removed.
//   - follow-up: case follow_up.appointmentStatus 'cancelled', cancelledBy 'doctor' (cancelledVisit /
//     cancelReasonCode read when present; scheduledDate / scheduledTime are the fallback).
//   - notification type 'appointment_cancelled_by_doctor', data { doctorName, reasonCode, caseId, … }.
// An MA cancelling for her physician writes changedBy 'ma' on the row; the case JSON says 'doctor'.
// Both read as "Dr. X cancelled" (Alex 2026-10-18). A cancel by anybody else (the patient, the system)
// is NOT a physician cancel and keeps today's handling.
// The reason reaches the patient only as one of three fixed sentences, by code; never free text.

export const CANCELLED = 'cancelled'
export const PHYSICIAN_CANCEL_NOTIFICATION = 'appointment_cancelled_by_doctor'
const PHYSICIAN_ACTORS = ['doctor', 'ma']

/** reasonCode -> en.json / es.json key of the patient sentence. Unknown codes have no sentence. */
export const CANCEL_REASON_KEYS = Object.freeze({
  physician_unavailable: 'caseHome.cancelReasonPhysicianUnavailable',
  practice_closed: 'caseHome.cancelReasonPracticeClosed',
  visit_not_needed: 'caseHome.cancelReasonVisitNotNeeded',
})

const norm = (v) => String(v ?? '').trim().toLowerCase()
const isCancelled = (status) => norm(status) === CANCELLED
const byPhysician = (who) => PHYSICIAN_ACTORS.includes(norm(who))
const reasonCodeOf = (o) => {
  const code = norm(o?.cancelReasonCode ?? o?.reasonCode)
  return Object.prototype.hasOwnProperty.call(CANCEL_REASON_KEYS, code) ? code : null
}

/** The i18n key of the reason sentence for a code, or null (no code, or one the app does not know). */
export function cancelReasonKey(code) {
  const c = norm(code)
  return Object.prototype.hasOwnProperty.call(CANCEL_REASON_KEYS, c) ? CANCEL_REASON_KEYS[c] : null
}

/** The case's appointment_request (initial visit) was cancelled by the physician. Object or JSON. */
export function requestCancelledByPhysician(appointmentRequest) {
  const r = asObject(appointmentRequest)
  return !!r && isCancelled(r.status) && byPhysician(r.cancelledBy)
}

/** The case's follow_up visit was cancelled by the physician. Object or JSON. */
export function followUpCancelledByPhysician(followUp) {
  const f = asObject(followUp)
  return !!f && isCancelled(f.appointmentStatus) && byPhysician(f.cancelledBy)
}

/** Either half of a case says the physician cancelled its visit. */
export function caseHasPhysicianCancel(caseItem) {
  if (!caseItem || typeof caseItem !== 'object') return false
  return requestCancelledByPhysician(caseItem.appointmentRequest || caseItem.appointment_request)
    || followUpCancelledByPhysician(caseItem.followUp || caseItem.follow_up)
}

/** { date, time, type, reasonCode } of a physician-cancelled initial visit, else null. */
export function physicianCancelOfRequest(appointmentRequest) {
  if (!requestCancelledByPhysician(appointmentRequest)) return null
  const r = asObject(appointmentRequest)
  const v = asObject(r.cancelledVisit) || {}
  return {
    date: v.date || r.confirmedDate || r.requestedDate || null,
    time: v.time || r.confirmedTime || r.requestedTime || null,
    type: v.type || r.confirmedType || r.appointmentType || null,
    reasonCode: reasonCodeOf(r),
  }
}

/** { date, time, type, reasonCode } of a physician-cancelled follow-up visit, else null. */
export function physicianCancelOfFollowUp(followUp) {
  if (!followUpCancelledByPhysician(followUp)) return null
  const f = asObject(followUp)
  const v = asObject(f.cancelledVisit) || {}
  return {
    date: v.date || f.scheduledDate || null,
    time: v.time || f.scheduledTime || null,
    type: v.type || f.visitType || null,
    reasonCode: reasonCodeOf(f),
  }
}

/**
 * The LATEST cancelled status_history entry of a row, whoever wrote it, else null (no history, or no
 * cancelled entry in it).
 */
function lastCancelEntry(row) {
  let hist = row?.status_history ?? row?.statusHistory
  if (typeof hist === 'string') { try { hist = JSON.parse(hist) } catch { hist = null } }
  if (!Array.isArray(hist)) return null
  for (let i = hist.length - 1; i >= 0; i--) {
    const e = hist[i]
    if (e && isCancelled(e.status)) return e
  }
  return null
}

/** The status_history entry that cancelled this row, when a physician or his MA wrote it, else null. */
function physicianCancelEntry(row) {
  const e = lastCancelEntry(row)
  return e && byPhysician(e.changedBy) ? e : null
}

/**
 * An appointments row (GET /appointments) the physician or his MA cancelled. The latest cancelled
 * history entry decides (its changedBy). modified_by is only the fallback for a row whose history has
 * no cancelled entry at all: it is left over from an earlier physician write, and the system and
 * patient cancels do not overwrite it (PROD-1137 review M1).
 */
export function rowCancelledByPhysician(row) {
  if (!row || !isCancelled(row.status)) return false
  const last = lastCancelEntry(row)
  if (last) return byPhysician(last.changedBy)
  return byPhysician(row.modified_by ?? row.modifiedBy)
}

/** The reason code of a physician-cancelled row, else null. */
export function rowCancelReasonCode(row) {
  return rowCancelledByPhysician(row) ? reasonCodeOf(physicianCancelEntry(row)) : null
}

/** The fields of the notification the app reads: { doctorName (no "Dr."), reasonCode, caseId, date, time }. */
export function physicianCancelNotice(notification) {
  const type = String(notification?.type || '').replace(/\./g, '_')
  if (type !== PHYSICIAN_CANCEL_NOTIFICATION) return null
  const data = asObject(notification?.data) || {}
  return {
    doctorName: String(data.doctorName || '').trim().replace(/^Dr\.?\s+/i, ''),
    doctorTitle: String(data.doctorTitle || '').trim() || null,   // s119: the API's title (393)
    reasonCode: reasonCodeOf(data),
    caseId: data.caseId || data.case_id || null,
    date: String(data.appointmentDate || '').trim() || null,
    time: String(data.appointmentTime || '').trim() || null,
  }
}

/**
 * The follow_up the scheduling screen books from. After a physician cancel it is a NEW booking: the
 * cancelled visit's slot and the cancel marks are dropped (no reschedule of a cancelled row, and the
 * new request does not carry "cancelled by the doctor"). Any other follow_up is returned unchanged.
 */
export function followUpForRebook(followUp) {
  if (!followUpCancelledByPhysician(followUp)) return followUp
  const f = asObject(followUp)
  const { cancelledBy, cancelledAt, cancelReasonCode, cancelledVisit, appointmentStatus, scheduledDate, scheduledTime, ...rest } = f
  return { ...rest, appointmentScheduled: false }
}

/** Rows a patient screen may list: a declined (rejected) row is not a visit. */
export function withoutDeclinedRows(rows) {
  return Array.isArray(rows) ? rows.filter((a) => !isDeclined(a?.status)) : []
}

/**
 * The store's follow-up merge. A follow_up the SERVER sent (serverSent) is the truth about whether a
 * follow-up visit is scheduled: when it carries neither appointmentScheduled nor scheduledDate (a
 * declined held reschedule clears both), the stale local scheduled fields are not put back over it.
 * Only when the server sent no follow_up does a local scheduled copy still fill the gap.
 */
export function mergeFollowUp(apiFollowUp, localFollowUp, serverSent) {
  if (apiFollowUp?.appointmentScheduled || apiFollowUp?.scheduledDate) return apiFollowUp
  if (serverSent && apiFollowUp) return apiFollowUp
  if (localFollowUp?.appointmentScheduled || localFollowUp?.scheduledDate) return { ...apiFollowUp, ...localFollowUp }
  return apiFollowUp || localFollowUp
}
